/**
 * Web Chats Share Page
 * Shows each tenant their unique public web capture bot URL, a live preview,
 * and an embed code snippet they can drop into their website.
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Tooltip,
  IconButton,
  Divider,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  ContentCopy,
  Check,
  OpenInNew,
  Language,
  QrCode2,
  Code,
  Visibility,
  Share,
  VerifiedUser,
  Public,
  Close,
  Palette,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import adminApi from '@/services/admin.api';
import StatusChip from '@/components/ui/StatusChip';
import apiService from '@/services/api.service';

// ─── AI agent selector (which agent powers the web capture bot) ──────────

function AgentSelectorCard() {
  const [agents, setAgents] = useState<Array<{ _id: string; name: string }>>([]);
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([adminApi.getAgents({ limit: 100, isActive: true }), adminApi.getLeadFormSettings()])
      .then(([ag, cfg]) => {
        const list = (((ag as any).data ?? ag) as Array<{ _id: string; name: string }>) || [];
        const options = list.map((a) => ({ _id: a._id, name: a.name }));
        // Only active agents are offered, but a previously chosen agent that has since
        // been deactivated must stay visible (flagged) so the select doesn't silently blank.
        const chosen = cfg.agentId;
        if (chosen && !options.some((o) => o._id === chosen)) {
          options.push({ _id: chosen, name: 'Selected agent (inactive)' });
        }
        setAgents(options);
        setAgentId(cfg.agentId ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (value: string) => {
    setAgentId(value);
    setSaving(true);
    try {
      await adminApi.updateLeadFormSettings({ agentId: value || null });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" gutterBottom display="block">
          AI agent — powers the chat
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Powered by"
            value={agentId}
            onChange={(e) => save(e.target.value)}
            disabled={loading || saving}
            helperText={
              loading
                ? 'Loading…'
                : agents.length === 0
                  ? 'No AI agents yet — create one under Agents.'
                  : agentId
                    ? 'The chatbot answers using this agent’s knowledge.'
                    : 'No agent selected — the chatbot uses a scripted flow.'
            }
          >
            <MenuItem value="">
              <em>None (scripted flow)</em>
            </MenuItem>
            {agents.map((a) => (
              <MenuItem key={a._id} value={a._id}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>
          {saving ? (
            <CircularProgress size={20} />
          ) : saved ? (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main', flexShrink: 0 }}>
              <Check sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600}>
                Saved
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Origin allow-list (optional per-tenant embedding restriction) ────────

function extractError(error: any, fallback: string): string {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

function WhitelistSiteTab() {
  const [origins, setOrigins] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi
      .getLeadFormSettings()
      .then((cfg) => setOrigins(cfg.allowedOrigins ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const persist = async (next: string[]) => {
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.updateLeadFormSettings({ allowedOrigins: next });
      // Trust the server's response as ground truth (it normalises casing and
      // dedupes) rather than the optimistic `next` we sent — keeps the chip list
      // exactly in sync with what's actually persisted.
      setOrigins(res.allowedOrigins ?? next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(extractError(e, 'Could not save — check the domain format.'));
    } finally {
      setSaving(false);
    }
  };

  const addOrigin = () => {
    const value = input.trim();
    if (!value) return;
    let bareOrigin: string;
    try {
      bareOrigin = new URL(value).origin;
    } catch {
      setError('Enter a full URL, e.g. https://example.com');
      return;
    }
    setInput('');
    if (origins.includes(bareOrigin)) return;
    void persist([...origins, bareOrigin]);
  };

  const removeOrigin = (origin: string) => {
    void persist(origins.filter((o) => o !== origin));
  };

  const restricted = origins.length > 0;

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        By default this chatbot can be embedded or called from any website. Add domains below to
        lock it down to only those sites — your own <code>{window.location.hostname}</code> embed
        is always allowed either way.
      </Alert>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              Allowed domains
            </Typography>
            {loading ? (
              <CircularProgress size={18} />
            ) : (
              <Chip
                size="small"
                label={restricted ? `Restricted — ${origins.length} domain${origins.length > 1 ? 's' : ''}` : 'Unrestricted'}
                color={restricted ? 'primary' : 'default'}
                variant={restricted ? 'filled' : 'outlined'}
                {...(restricted ? { icon: <VerifiedUser fontSize="small" /> } : {})}
              />
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="https://your-client-site.com"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOrigin();
                }
              }}
              disabled={loading || saving}
              error={!!error}
              helperText={error || 'The scheme (https://) and exact domain must match — no wildcards.'}
              InputProps={{
                startAdornment: <Public fontSize="small" sx={{ color: 'text.disabled', mr: 1 }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={addOrigin}
              disabled={loading || saving || !input.trim()}
              sx={{ flexShrink: 0, minWidth: 96 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Add site'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {origins.length > 0 ? (
            <Stack spacing={1}>
              {origins.map((o) => (
                <Stack
                  key={o}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  <Public fontSize="small" sx={{ color: '#4f46e5', flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', color: '#334155', flex: 1, wordBreak: 'break-all' }}
                  >
                    {o}
                  </Typography>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton size="small" onClick={() => removeOrigin(o)} disabled={saving}>
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          ) : (
            !loading && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                }}
              >
                <Public sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No domains added — this bot is embeddable anywhere.</Typography>
              </Box>
            )
          )}

          {saved && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1.5 }}>
              <Check fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Saved
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

// ─── Appearance (widget display copy) ────────────────────────────────────────

/** Mirrors LeadCapture.tsx's built-in defaults. Shown as input placeholders so an
 *  empty box visibly means "using this", and clearing a box reverts to it. */
const APPEARANCE_FIELDS = [
  {
    key: 'botName' as const,
    label: 'Bot name',
    helper: 'Shown in the chat header. Leave empty to use your organization name.',
    maxLength: 60,
    rows: 1,
  },
  {
    key: 'statusText' as const,
    label: 'Status line',
    helper: 'The small line under the name.',
    placeholder: 'Typically replies instantly',
    maxLength: 80,
    rows: 1,
  },
  {
    key: 'welcomeMessage' as const,
    label: 'Welcome message',
    helper: 'The first thing a visitor sees, before they type anything.',
    maxLength: 500,
    rows: 3,
  },
];

const DEFAULT_PRIMARY_COLOR = '#4f46e5';
const DEFAULT_SECONDARY_COLOR = '#818cf8';
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Swatch (opens the native colour picker) + hex text field, kept in sync both
 *  ways. No design-system colour picker exists in this app yet, so this is the
 *  standard zero-dependency approach: the native `<input type="color">` handles
 *  the actual picking UI, the text field handles precise/typed hex entry. */
function ColorField({
  label,
  value,
  fallback,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  fallback: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const swatchColor = HEX_COLOR_RE.test(value) ? value : fallback;
  return (
    <Stack spacing={0.75}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            borderRadius: 2,
            flexShrink: 0,
            border: '1.5px solid #e2e8f0',
            overflow: 'hidden',
            background: swatchColor,
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <input
            type="color"
            value={swatchColor}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            aria-label={`${label} picker`}
            style={{
              position: 'absolute',
              inset: -4,
              width: 'calc(100% + 8px)',
              height: 'calc(100% + 8px)',
              border: 'none',
              padding: 0,
              cursor: disabled ? 'default' : 'pointer',
              opacity: 0,
            }}
          />
        </Box>
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={fallback}
          disabled={disabled}
          error={!!value && !HEX_COLOR_RE.test(value)}
          sx={{ width: 130 }}
          inputProps={{ maxLength: 7, style: { fontFamily: 'monospace' } }}
        />
      </Stack>
    </Stack>
  );
}

/** Brand colours — read from and saved to the SAME /auth/organization endpoint
 *  MyAccount.tsx's Organization card uses (see api.service.ts's `patch`), just
 *  from this screen instead: it's the only place they're actually rendered
 *  (the widget header gradient), so editing them next to that live preview beats
 *  editing them blind on the account page. Deliberately its own card with its
 *  own save — a distinct mutation from the copy fields above, not bundled into
 *  the same "Save changes" click. `name`/`logoUrl` are never sent from here, so
 *  a save can't touch either (see auth.service.ts's updateOrganization: a
 *  branding field is only written when the request actually includes it).
 */
function BrandColorsCard({
  colors,
  onSaved,
}: {
  colors: { primaryColor: string; secondaryColor: string };
  onSaved: (next: { primaryColor: string; secondaryColor: string }) => void;
}) {
  const [values, setValues] = useState(colors);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setValues(colors), [colors.primaryColor, colors.secondaryColor]);

  const dirty = values.primaryColor !== colors.primaryColor || values.secondaryColor !== colors.secondaryColor;
  const invalid =
    (!!values.primaryColor && !HEX_COLOR_RE.test(values.primaryColor)) ||
    (!!values.secondaryColor && !HEX_COLOR_RE.test(values.secondaryColor));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await apiService.patch('/auth/organization', {
        branding: { primaryColor: values.primaryColor, secondaryColor: values.secondaryColor },
      });
      onSaved(values);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (e: any) {
      setError(extractError(e, 'Could not save — please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Brand colours
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Used for the chat header and message bubbles in the widget preview to the right.
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap mb={2}>
          <ColorField
            label="Primary"
            value={values.primaryColor}
            fallback={DEFAULT_PRIMARY_COLOR}
            disabled={saving}
            onChange={(next) => {
              setValues((v) => ({ ...v, primaryColor: next }));
              if (error) setError('');
            }}
          />
          <ColorField
            label="Secondary"
            value={values.secondaryColor}
            fallback={DEFAULT_SECONDARY_COLOR}
            disabled={saving}
            onChange={(next) => {
              setValues((v) => ({ ...v, secondaryColor: next }));
              if (error) setError('');
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !dirty || invalid}
            sx={{ minWidth: 120 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save colours'}
          </Button>
          {justSaved && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main' }}>
              <Check sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600}>
                Saved
              </Typography>
            </Stack>
          )}
          {invalid && !error && (
            <Typography variant="caption" color="error">
              Enter a 6-digit hex colour, e.g. #4f46e5
            </Typography>
          )}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function AppearanceTab() {
  const { tenant } = useAuth();
  const [values, setValues] = useState({ botName: '', statusText: '', welcomeMessage: '' });
  const [saved, setSaved] = useState({ botName: '', statusText: '', welcomeMessage: '' });
  const [colors, setColors] = useState({ primaryColor: '', secondaryColor: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([adminApi.getLeadFormSettings(), apiService.getCurrentUser()])
      .then(([cfg, me]) => {
        const next = {
          botName: cfg.botName ?? '',
          statusText: cfg.statusText ?? '',
          welcomeMessage: cfg.welcomeMessage ?? '',
        };
        setValues(next);
        setSaved(next);
        const branding = me?.data?.tenant?.branding;
        setColors({
          primaryColor: branding?.primaryColor ?? '',
          secondaryColor: branding?.secondaryColor ?? '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const dirty = (Object.keys(values) as Array<keyof typeof values>).some(
    (k) => values[k].trim() !== saved[k].trim()
  );

  const orgName = tenant?.name ?? 'your organization';
  const previewName = values.botName.trim() || orgName;
  const previewStatus = values.statusText.trim() || 'Typically replies instantly';
  const previewWelcome =
    values.welcomeMessage.trim() ||
    `Hi! 👋 Welcome to ${previewName}. Ask me anything — or share your name and number and we'll get back to you.`;
  const previewPrimary = colors.primaryColor || DEFAULT_PRIMARY_COLOR;
  const previewSecondary = colors.secondaryColor || DEFAULT_SECONDARY_COLOR;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.updateLeadFormSettings({
        botName: values.botName.trim(),
        statusText: values.statusText.trim(),
        welcomeMessage: values.welcomeMessage.trim(),
      });
      const next = {
        botName: res.botName ?? '',
        statusText: res.statusText ?? '',
        welcomeMessage: res.welcomeMessage ?? '',
      };
      setValues(next);
      setSaved(next);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (e: any) {
      setError(extractError(e, 'Could not save — please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Customise what visitors see. Leave a field empty to use the default shown in it.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Fields + brand colours */}
        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Card>
            <CardContent>
              <Stack spacing={2.5}>
                {APPEARANCE_FIELDS.map((f) => (
                  <TextField
                    key={f.key}
                    label={f.label}
                    value={values[f.key]}
                    onChange={(e) => {
                      setValues((v) => ({ ...v, [f.key]: e.target.value }));
                      if (error) setError('');
                    }}
                    // botName's placeholder is the live org name (its actual
                    // fallback); the others carry their own literal default.
                    placeholder={f.key === 'botName' ? orgName : (f.placeholder ?? '')}
                    helperText={f.helper}
                    disabled={loading || saving}
                    size="small"
                    fullWidth
                    multiline={f.rows > 1}
                    {...(f.rows > 1 ? { rows: f.rows } : {})}
                    inputProps={{ maxLength: f.maxLength }}
                  />
                ))}

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={save}
                    disabled={loading || saving || !dirty}
                    sx={{ minWidth: 120 }}
                  >
                    {saving ? <CircularProgress size={18} color="inherit" /> : 'Save changes'}
                  </Button>
                  {justSaved && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main', flexShrink: 0 }}>
              <Check sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600}>
                Saved
              </Typography>
            </Stack>
                  )}
                  {error && (
                    <Typography variant="caption" color="error">
                      {error}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Separate card/div: a distinct save action against a different
              endpoint (/auth/organization, not lead-form-settings). */}
          {!loading && (
            <BrandColorsCard
              colors={colors}
              onSaved={(next) => setColors(next)}
            />
          )}
        </Stack>

        {/* Live preview — mirrors the real widget header + first bubble, using
            the ACTUAL saved brand colours (not a hardcoded placeholder). */}
        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
            PREVIEW
          </Typography>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                px: 2,
                py: 1.5,
                color: '#fff',
                background: `linear-gradient(135deg, ${previewPrimary}, ${previewSecondary})`,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {previewName.charAt(0).toUpperCase()}
              </Box>
              <Box minWidth={0}>
                <Typography fontWeight={700} fontSize="0.9rem" noWrap>
                  {previewName}
                </Typography>
                <Typography fontSize="0.7rem" sx={{ opacity: 0.9 }} noWrap>
                  ● {previewStatus}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ p: 2, background: '#fbfcfe', minHeight: 110 }}>
              <Box
                sx={{
                  background: '#f1f5f9',
                  borderRadius: '16px 16px 16px 4px',
                  px: 1.5,
                  py: 1,
                  fontSize: '0.8125rem',
                  color: '#0f172a',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {previewWelcome}
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            This is exactly how the header and colours will look on your live widget.
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function useCopy(text: string, ms = 2000) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), ms);
    });
  };
  return { copied, copy };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LeadForm() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState(0);

  const slug = tenant?.subdomain ?? '';
  const formUrl = `${window.location.origin}/form/${slug}`;

  const embedCode = `<!-- ${tenant?.name ?? 'Lead'} Capture Form -->
<iframe
  src="${formUrl}"
  width="100%"
  height="700"
  frameborder="0"
  style="border-radius:16px; max-width:520px; display:block; margin:0 auto;"
  title="Contact Form"
  loading="lazy"
></iframe>`;

  const { copied: urlCopied, copy: copyUrl } = useCopy(formUrl);
  const { copied: codeCopied, copy: copyCode } = useCopy(embedCode);

  if (!slug) {
    return (
      <Box p={3}>
        <Alert severity="warning">Tenant information is not available. Please log out and log in again.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth={960} mx="auto">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4f46e5, #818cf8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <Language />
        </Box>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="h5" fontWeight={700}>
              Public Web Capture Bot
            </Typography>
            <StatusChip status="active" label="Live" dot />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Share this link on your website, social media, or embed it directly on any page.
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 3 }} />

      <AgentSelectorCard />

      {/* ── URL card ────────────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" gutterBottom display="block">
            Your unique form URL
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Box
              sx={{
                flex: 1,
                background: '#f8fafc',
                border: '1.5px solid #e2e8f0',
                borderRadius: 2,
                px: 2,
                py: 1.25,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                color: '#4f46e5',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formUrl}
            </Box>
            <Stack direction="row" spacing={1} flexShrink={0}>
              <Tooltip title={urlCopied ? 'Copied!' : 'Copy link'}>
                <Button
                  variant="contained"
                  startIcon={urlCopied ? <Check /> : <ContentCopy />}
                  onClick={copyUrl}
                  color={urlCopied ? 'success' : 'primary'}
                  sx={{ minWidth: 130 }}
                >
                  {urlCopied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton
                  onClick={() => window.open(formUrl, '_blank')}
                  sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2 }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Quick share options */}
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            QUICK SHARE
          </Typography>
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Share />}
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: `${tenant?.name} — Contact Form`, url: formUrl });
                } else {
                  copyUrl();
                }
              }}
            >
              Share
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                window.open(
                  `https://wa.me/?text=${encodeURIComponent(`Fill in your details here: ${formUrl}`)}`,
                  '_blank'
                )
              }
            >
              WhatsApp
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                window.open(
                  `mailto:?subject=Contact%20Us&body=${encodeURIComponent(
                    `You can reach us via this form: ${formUrl}`
                  )}`,
                  '_blank'
                )
              }
            >
              Email
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Tabs: Preview / Embed ────────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<Visibility fontSize="small" />} iconPosition="start" label="Live Preview" />
        <Tab icon={<Palette fontSize="small" />} iconPosition="start" label="Appearance" />
        <Tab icon={<Code fontSize="small" />} iconPosition="start" label="Embed on Website" />
        <Tab icon={<VerifiedUser fontSize="small" />} iconPosition="start" label="Whitelist your Site" />
        <Tab icon={<QrCode2 fontSize="small" />} iconPosition="start" label="How to Use" />
      </Tabs>

      {/* ── Preview tab ─────────────────────────────────────────────────── */}
      {tab === 0 && (
        <Card>
          <CardContent sx={{ p: '0 !important' }}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)',
                borderRadius: '11px 11px 0 0',
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              {/* Browser chrome dots */}
              {['#ef4444', '#f59e0b', '#10b981'].map((c) => (
                <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
              ))}
              <Box
                sx={{
                  flex: 1,
                  background: '#fff',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.4,
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                  fontFamily: 'monospace',
                  ml: 1,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formUrl}
              </Box>
            </Box>
            <Box sx={{ height: 680, borderRadius: '0 0 11px 11px', overflow: 'hidden' }}>
              <iframe
                src={formUrl}
                width="100%"
                height="100%"
                style={{ border: 'none', display: 'block' }}
                title="Web Capture Bot Preview"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Appearance tab ──────────────────────────────────────────────── */}
      {tab === 1 && <AppearanceTab />}

      {/* ── Embed tab ───────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Copy the code below and paste it anywhere in your website's HTML — a landing page,
            contact page, or sidebar.
          </Alert>

          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  HTML Embed Code
                </Typography>
                <Button
                  size="small"
                  variant={codeCopied ? 'contained' : 'outlined'}
                  color={codeCopied ? 'success' : 'primary'}
                  startIcon={codeCopied ? <Check /> : <ContentCopy />}
                  onClick={copyCode}
                >
                  {codeCopied ? 'Copied!' : 'Copy Code'}
                </Button>
              </Stack>
              <Paper
                variant="outlined"
                sx={{
                  background: '#0d1117',
                  borderRadius: 2,
                  p: 2,
                  overflowX: 'auto',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                    fontSize: '0.8125rem',
                    color: '#c9d1d9',
                    lineHeight: 1.7,
                    whiteSpace: 'pre',
                  }}
                >
                  <span style={{ color: '#8b949e' }}>{`<!-- ${tenant?.name ?? 'Lead'} Capture Form -->`}</span>{'\n'}
                  <span style={{ color: '#7ee787' }}>{`<iframe`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>src</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"${formUrl}"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>width</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"100%"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>height</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"700"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>frameborder</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"0"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>style</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"border-radius:16px; max-width:520px; display:block; margin:0 auto;"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>title</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"Contact Form"`}</span>{'\n'}
                  {'  '}<span style={{ color: '#79c0ff' }}>loading</span><span style={{ color: '#c9d1d9' }}>=</span><span style={{ color: '#a5d6ff' }}>{`"lazy"`}</span>{'\n'}
                  <span style={{ color: '#7ee787' }}>{`></iframe>`}</span>
                </pre>
              </Paper>

              <Divider sx={{ my: 2.5 }} />

              <SourceTagBuilder formUrl={formUrl} />
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* ── Whitelist your Site tab ─────────────────────────────────────── */}
      {tab === 3 && <WhitelistSiteTab />}

      {/* ── How to Use tab ──────────────────────────────────────────────── */}
      {tab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              How it works
            </Typography>
            <Stack spacing={3} mt={1}>
              {[
                {
                  step: '1',
                  title: 'Share your link',
                  desc: 'Copy the form URL above and paste it in your Instagram bio, WhatsApp status, Google Business page, or anywhere you talk to customers.',
                  color: '#4f46e5',
                },
                {
                  step: '2',
                  title: 'Customer chats with your assistant',
                  desc: 'Visitors enter their name, WhatsApp number, and an optional message. No login needed — it takes under a minute.',
                  color: '#10b981',
                },
                {
                  step: '3',
                  title: 'Lead appears instantly',
                  desc: 'The contact is automatically created in your Leads list. Source is detected from UTM params or the referring website.',
                  color: '#f59e0b',
                },
                {
                  step: '4',
                  title: 'Workflow triggers automatically',
                  desc: 'Any active Automation Workflow with a "Lead Created" trigger fires immediately — send a welcome WhatsApp, assign an agent, or start a follow-up sequence.',
                  color: '#ef4444',
                },
              ].map(({ step, title, desc, color }) => (
                <Stack key={step} direction="row" spacing={2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: color,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {step}
                  </Box>
                  <Box>
                    <Typography fontWeight={600} mb={0.25}>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                      {desc}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Tips to get more leads
            </Typography>
            <Stack spacing={1} mt={1}>
              {[
                'Pin the link in your WhatsApp Business profile',
                'Add a "Contact Us" button on your website that opens the form',
                'Share the link in your email signature',
                'Put it in your Google Business "Appointments" URL field',
                'Use UTM tags so you know exactly which channel each lead came from',
              ].map((tip) => (
                <Stack key={tip} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#4f46e5',
                      mt: '7px',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {tip}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ─── Source tagging ───────────────────────────────────────────────────────────

/** Starting points, not a fixed list — each is editable in place. Kept to the
 *  bare `utm_source` value because that is the ONLY param the backend's
 *  detectSource() classifies on (see public.routes.ts); utm_medium/campaign are
 *  still stored on the lead's metadata but don't change the source category, so
 *  putting them in the presets implied a precision that isn't there. */
const SOURCE_PRESETS = ['homepage', 'google', 'instagram', 'whatsapp'];

function SourceTagBuilder({ formUrl }: { formUrl: string }) {
  const [tag, setTag] = useState(SOURCE_PRESETS[0]!);
  const trimmed = tag.trim();
  const taggedUrl = trimmed ? `${formUrl}?utm_source=${encodeURIComponent(trimmed)}` : formUrl;
  const { copied, copy } = useCopy(taggedUrl);

  return (
    <>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Tag your traffic source (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Use a different link in each place you share the bot, and every lead it captures is
        labelled with where it came from — visible in the Source column under Web Captures.
      </Typography>

      <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
        {SOURCE_PRESETS.map((p) => (
          <Chip
            key={p}
            label={p}
            size="small"
            onClick={() => setTag(p)}
            color={trimmed === p ? 'primary' : 'default'}
            variant={trimmed === p ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label="Source name"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="e.g. newsletter"
          sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            px: 2,
            py: 1,
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            color: '#4f46e5',
            wordBreak: 'break-all',
          }}
        >
          {formUrl}
          {trimmed && <strong>{`?utm_source=${trimmed}`}</strong>}
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
          <IconButton
            onClick={copy}
            sx={{ border: '1.5px solid #e2e8f0', borderRadius: 2, flexShrink: 0 }}
          >
            {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </>
  );
}
